import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';

const TOUR_STORAGE_KEY = 'ff_tour_completed_';

/**
 * OnboardingTour — renders an interactive step-by-step tooltip tour.
 *
 * Props:
 *   steps: Array<{ target: string (CSS selector), title: string, description: string, position?: 'top'|'bottom'|'left'|'right' }>
 *   tourKey: string — unique key stored in localStorage to prevent re-showing
 *   onComplete: () => void
 *   onSkip: () => void
 */
const OnboardingTour = ({ steps = [], tourKey = 'default', onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);
  const animFrameRef = useRef(null);

  const measureTarget = useCallback(() => {
    const step = steps?.[currentStep];
    if (!step?.target) return;
    const el = document.querySelector(step?.target);
    if (el) {
      const rect = el?.getBoundingClientRect();
      setTargetRect({
        top: rect?.top + window.scrollY,
        left: rect?.left + window.scrollX,
        width: rect?.width,
        height: rect?.height,
        viewTop: rect?.top,
        viewLeft: rect?.left,
      });
      // Scroll element into view smoothly
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTargetRect(null);
    }
  }, [currentStep, steps]);

  useEffect(() => {
    // Small delay to let layout settle after scroll
    const t = setTimeout(() => {
      measureTarget();
      setVisible(true);
    }, 350);
    return () => clearTimeout(t);
  }, [measureTarget]);

  useEffect(() => {
    const handleResize = () => measureTarget();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [measureTarget]);

  const handleNext = () => {
    if (currentStep < steps?.length - 1) {
      setVisible(false);
      setTimeout(() => {
        setCurrentStep((s) => s + 1);
      }, 200);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setVisible(false);
      setTimeout(() => {
        setCurrentStep((s) => s - 1);
      }, 200);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(TOUR_STORAGE_KEY + tourKey, 'true');
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem(TOUR_STORAGE_KEY + tourKey, 'true');
    onSkip?.();
  };

  const getTooltipPosition = () => {
    if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const step = steps?.[currentStep];
    const position = step?.position || 'bottom';
    const GAP = 16;
    const TW = 320; // tooltip width approx
    const TH = 180; // tooltip height approx
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top, left;

    if (position === 'bottom') {
      top = targetRect?.viewTop + targetRect?.height + GAP;
      left = targetRect?.viewLeft + targetRect?.width / 2 - TW / 2;
    } else if (position === 'top') {
      top = targetRect?.viewTop - TH - GAP;
      left = targetRect?.viewLeft + targetRect?.width / 2 - TW / 2;
    } else if (position === 'right') {
      top = targetRect?.viewTop + targetRect?.height / 2 - TH / 2;
      left = targetRect?.viewLeft + targetRect?.width + GAP;
    } else if (position === 'left') {
      top = targetRect?.viewTop + targetRect?.height / 2 - TH / 2;
      left = targetRect?.viewLeft - TW - GAP;
    }

    // Clamp within viewport
    left = Math.max(12, Math.min(left, vw - TW - 12));
    top = Math.max(12, Math.min(top, vh - TH - 12));

    return { position: 'fixed', top, left };
  };

  const getArrowStyle = () => {
    const step = steps?.[currentStep];
    const position = step?.position || 'bottom';
    const base = {
      position: 'absolute',
      width: 0,
      height: 0,
      pointerEvents: 'none',
    };
    if (position === 'bottom') return { ...base, top: -8, left: '50%', transform: 'translateX(-50%)', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid rgba(255,255,255,0.08)' };
    if (position === 'top') return { ...base, bottom: -8, left: '50%', transform: 'translateX(-50%)', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid rgba(255,255,255,0.08)' };
    if (position === 'right') return { ...base, left: -8, top: '50%', transform: 'translateY(-50%)', borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: '8px solid rgba(255,255,255,0.08)' };
    if (position === 'left') return { ...base, right: -8, top: '50%', transform: 'translateY(-50%)', borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '8px solid rgba(255,255,255,0.08)' };
    return base;
  };

  const step = steps?.[currentStep];
  const tooltipPos = getTooltipPosition();
  const isLast = currentStep === steps?.length - 1;

  const HIGHLIGHT_PAD = 8;

  return createPortal(
    <>
      {/* Dark overlay with cutout */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          pointerEvents: 'none',
        }}
      >
        {/* Top */}
        {targetRect && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: Math.max(0, targetRect?.viewTop - HIGHLIGHT_PAD),
            background: 'rgba(10,10,11,0.75)',
            backdropFilter: 'blur(1px)',
          }} />
        )}
        {/* Bottom */}
        {targetRect && (
          <div style={{
            position: 'absolute',
            top: targetRect?.viewTop + targetRect?.height + HIGHLIGHT_PAD,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(10,10,11,0.75)',
            backdropFilter: 'blur(1px)',
          }} />
        )}
        {/* Left */}
        {targetRect && (
          <div style={{
            position: 'absolute',
            top: Math.max(0, targetRect?.viewTop - HIGHLIGHT_PAD),
            left: 0,
            width: Math.max(0, targetRect?.viewLeft - HIGHLIGHT_PAD),
            height: targetRect?.height + HIGHLIGHT_PAD * 2,
            background: 'rgba(10,10,11,0.75)',
            backdropFilter: 'blur(1px)',
          }} />
        )}
        {/* Right */}
        {targetRect && (
          <div style={{
            position: 'absolute',
            top: Math.max(0, targetRect?.viewTop - HIGHLIGHT_PAD),
            left: targetRect?.viewLeft + targetRect?.width + HIGHLIGHT_PAD,
            right: 0,
            height: targetRect?.height + HIGHLIGHT_PAD * 2,
            background: 'rgba(10,10,11,0.75)',
            backdropFilter: 'blur(1px)',
          }} />
        )}
        {/* Full overlay when no target */}
        {!targetRect && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,11,0.75)' }} />
        )}
      </div>

      {/* Highlight ring around target */}
      {targetRect && (
        <div
          style={{
            position: 'fixed',
            top: targetRect?.viewTop - HIGHLIGHT_PAD,
            left: targetRect?.viewLeft - HIGHLIGHT_PAD,
            width: targetRect?.width + HIGHLIGHT_PAD * 2,
            height: targetRect?.height + HIGHLIGHT_PAD * 2,
            borderRadius: 12,
            border: '2px solid #AAFF00',
            boxShadow: '0 0 0 4px rgba(170,255,0,0.12), 0 0 24px rgba(170,255,0,0.2)',
            zIndex: 9999,
            pointerEvents: 'none',
            transition: 'all 0.3s ease',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        style={{
          ...tooltipPos,
          zIndex: 10000,
          width: 320,
          background: 'rgba(18,18,20,0.97)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 16,
          padding: '20px 22px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(170,255,0,0.08)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(6px)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          fontFamily: "'Inter Tight', 'Inter', sans-serif",
        }}
      >
        {/* Arrow */}
        <div style={getArrowStyle()} />

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: 'rgba(170,255,0,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon name={step?.icon || 'Lightbulb'} size={14} color="#AAFF00" />
            </div>
            <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }}>
              {currentStep + 1} / {steps?.length}
            </span>
          </div>
          <button
            onClick={handleSkip}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 2, lineHeight: 1 }}
            title="Skip tour"
          >
            <Icon name="X" size={14} />
          </button>
        </div>

        {/* Title */}
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', marginBottom: 6, letterSpacing: '-0.01em' }}>
          {step?.title}
        </h3>

        {/* Description */}
        <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 18 }}>
          {step?.description}
        </p>

        {/* Progress dots */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
          {steps?.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === currentStep ? 20 : 6,
                height: 6,
                borderRadius: 3,
                background: i === currentStep ? '#AAFF00' : i < currentStep ? 'rgba(170,255,0,0.35)' : 'rgba(255,255,255,0.12)',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <button
            onClick={handleSkip}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)',
              padding: '6px 0', fontFamily: 'inherit',
            }}
          >
            Skip tour
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  padding: '7px 14px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.6)',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                background: '#AAFF00',
                border: 'none',
                borderRadius: 8,
                padding: '7px 18px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                color: '#0A0A0B',
                fontFamily: 'inherit',
                fontWeight: 700,
                boxShadow: '0 0 12px rgba(170,255,0,0.3)',
              }}
            >
              {isLast ? 'Done ✓' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export { TOUR_STORAGE_KEY };
export default OnboardingTour;
